/**
 * Mapeo entre nombres de propietarios (pestañas del Excel) y nombres de propiedades (base de datos)
 * 
 * Este mapeo se usa para el BookingExcelImporter para relacionar las 37 pestañas del Excel
 * con las propiedades correspondientes en Supabase.
 */

export interface PropertyMapping {
  /** Nombre del propietario (como aparece en la pestaña del Excel) */
  ownerName: string;
  /** Nombre de la propiedad (como está en la base de datos) */
  propertyName: string;
  /** Nombre de la pestaña completa en el Excel (opcional, para referencia) */
  sheetName?: string;
}

/**
 * Mapeo completo de propietarios a propiedades
 * Actualizado: 10 de Noviembre, 2025
 */
export const PROPERTY_MAPPINGS: PropertyMapping[] = [
  { ownerName: 'INAKI HARO', propertyName: '1880 Haro Old Town House' },
  { ownerName: 'GORKA', propertyName: 'Ayamendi House' },
  { ownerName: 'IVONE', propertyName: 'Basurtos Urban Apartment' },
  { ownerName: 'LORENZO', propertyName: 'Deusto City Center' },
  { ownerName: 'BUSTURIA', propertyName: 'Busturia Etxea' },
  { ownerName: 'AGURTZANE', propertyName: 'Casa Mawi' },
  { ownerName: 'DEUSTO NAIARA', propertyName: 'Deusto Apartment' },
  { ownerName: 'AITOR', propertyName: 'Flowers Family Home' },
  { ownerName: 'NACHA', propertyName: 'Gorliz Beach House' },
  { ownerName: 'OSCAR SASTRE', propertyName: 'La Antigua Sastrería' },
  { ownerName: 'FRANCISCO LOFT IN', propertyName: 'Loft in Bidezabal' },
  { ownerName: 'KONPORTE', propertyName: 'Loft in Getxo' },
  { ownerName: 'URIBARRI NAIARA', propertyName: 'Loft in Uribarri' },
  { ownerName: 'ANA DE KEY', propertyName: 'Los Angeles House' },
  { ownerName: 'PLENCIA SUITE', propertyName: 'Plencia Suite' },
  { ownerName: 'MARI CARMEN', propertyName: 'República de Begoña' },
  { ownerName: 'ASIER SARRIKOBASO', propertyName: 'Sarrikobaso House' },
  { ownerName: 'IGNACIO SOPELMAR 4', propertyName: 'Sopela Sea House' },
  { ownerName: 'SOPELA URBAN', propertyName: 'Sopela Urban City' },
  { ownerName: 'ASIER TELLETXE', propertyName: 'Telletxe Residence' },
  { ownerName: '1. HIRI ZAHAR SUIT', propertyName: 'Hiri Zahar Suite 1' },
  { ownerName: '2. HIRI ZAHAR SUIT', propertyName: 'Hiri Zahar Suite 2' },
  { ownerName: '3. HIRI ZAHAR SUIT', propertyName: 'Hiri Zahar Suite 3' },
  { ownerName: 'SILVIA LIENDO', propertyName: 'Villalindo' },
  { ownerName: 'OLGA LIENDO', propertyName: 'La estancia del valle' },
  { ownerName: '1. GORKA LAREDO', propertyName: 'Laredo Suite 1' },
  { ownerName: '2. GORKA LAREDO', propertyName: 'Laredo Suite 2' },
  { ownerName: 'ORTUELLA', propertyName: 'Ortuella Apartment' },
  { ownerName: 'MATIKO', propertyName: 'Matiko Apartment' },
  { ownerName: 'GABIKA 1', propertyName: 'Gabika Apartment 1' },
  { ownerName: 'GABIKA 2', propertyName: 'Gabika Apartment 2' },
  { ownerName: 'GABIKA 3', propertyName: 'Gabika Apartment 3' },
  { ownerName: 'GABIKA 4', propertyName: 'Gabika Apartment 4' },
  { ownerName: 'LUIS SOPELMAR', propertyName: 'Sopela Ocean House' },
  { ownerName: 'RIVERSIDE', propertyName: 'Bilbao Riverside' },
  { ownerName: 'ANGEL BERANGO', propertyName: 'Neguri Apartment' },
];

/**
 * Busca el nombre de la propiedad a partir del nombre del propietario
 */
export function getPropertyNameByOwner(ownerName: string): string | null {
  const normalized = ownerName.trim().toLowerCase();
  
  const mapping = PROPERTY_MAPPINGS.find(m => 
    m.ownerName.toLowerCase() === normalized
  );
  
  return mapping ? mapping.propertyName : null;
}

/**
 * Busca el nombre del propietario a partir del nombre de la propiedad
 */
export function getOwnerByPropertyName(propertyName: string): string | null {
  const normalized = propertyName.trim().toLowerCase();
  
  const mapping = PROPERTY_MAPPINGS.find(m => 
    m.propertyName.toLowerCase() === normalized
  );
  
  return mapping ? mapping.ownerName : null;
}

/**
 * Extrae el nombre del propietario desde el nombre de una pestaña del Excel
 * Ejemplo: "1.  RESERVAS INAKI HARO" → "INAKI HARO"
 * Ejemplo: "RESERVAS ANGEL BERANGO" → "ANGEL BERANGO"
 */
export function extractOwnerFromSheetName(sheetName: string): string | null {
  // Remover número inicial (puede tener uno o más espacios)
  let name = sheetName.replace(/^\d+\.\s+/, ''); // Quitar "1.  " o "1. "
  
  // Remover "RESERVAS" y espacios
  name = name.replace(/^RESERVAS\s+/i, ''); // Quitar "RESERVAS "
  
  // Limpiar espacios extra al final (algunos nombres tienen espacios al final)
  name = name.trim();
  
  // Buscar coincidencia exacta (case-insensitive)
  const normalized = name.toUpperCase();
  const mapping = PROPERTY_MAPPINGS.find(m => 
    m.ownerName.toUpperCase() === normalized
  );
  
  if (mapping) return mapping.ownerName;
  
  // Buscar coincidencia parcial (para casos como "FRANCISCO LOFT IN " con espacio al final)
  const partialMapping = PROPERTY_MAPPINGS.find(m =>
    m.ownerName.toUpperCase().includes(normalized) ||
    normalized.includes(m.ownerName.toUpperCase())
  );
  
  return partialMapping ? partialMapping.ownerName : null;
}

/**
 * Obtiene todas las propiedades únicas del mapeo
 */
export function getAllPropertyNames(): string[] {
  return [...new Set(PROPERTY_MAPPINGS.map(m => m.propertyName))];
}

/**
 * Obtiene todos los propietarios únicos del mapeo
 */
export function getAllOwnerNames(): string[] {
  return [...new Set(PROPERTY_MAPPINGS.map(m => m.ownerName))];
}

/**
 * Estadísticas del mapeo
 */
export function getMappingStats() {
  return {
    totalMappings: PROPERTY_MAPPINGS.length,
    uniqueProperties: getAllPropertyNames().length,
    uniqueOwners: getAllOwnerNames().length,
  };
}
